import { CustomErrorHandler } from '@utils/custom-error-handler';
import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { from, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { LoginService } from '@services/login.service';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '@services/auth.service';

export const appInterceptorFn: HttpInterceptorFn = (req, next) => {
  const loginService = inject(LoginService);
  const authService = inject(AuthService);
  const toastr = inject(ToastrService);

  const isOidcRequest = req.url.includes('/oauth2/') || req.url.includes('/oidc/');
  if (isOidcRequest) {
    return next(req);
  }

  const authenticatedHttpCall = req.headers.get('No-Auth') !== 'True';
  let request = req;

  if (authenticatedHttpCall) {
    const defaultHeaders: Record<string, string> = { 'Accept': '*/*' };

    if (localStorage.idEmpresaLogada != null && !req.headers.get('api-company-id')) {
      defaultHeaders['api-company-id'] = localStorage.idEmpresaLogada;
    }
    if (localStorage.cnpj != null && !req.headers.get('api-cnpj')) {
      defaultHeaders['api-cnpj'] = localStorage.cnpj;
    }
    if (localStorage.apiUserId != null && !req.headers.get('api-user-id')) {
      defaultHeaders['api-user-id'] = localStorage.apiUserId;
    }
    if (
      !req.headers.has('Content-Type') &&
      req.method !== 'GET' &&
      req.body &&
      req.body?.constructor?.name !== 'FormData'
    ) {
      defaultHeaders['Content-Type'] = 'application/x-www-form-urlencoded; charset=utf-8';
    }

    const headers = Object.entries(defaultHeaders).reduce((h, [key, value]) => {
      return h.has(key) ? h.append(key, value) : h.set(key, value);
    }, req.headers);

    request = req.clone({
      setHeaders: headers.keys().reduce((acc: Record<string, any>, key) => {
        acc[key] = headers.getAll(key);
        return acc;
      }, {})
    });
  }

  return next(request).pipe(
    map(response => {
      if (response instanceof HttpResponse) {
        if (response.headers?.get('www-authenticate')) {
          response.headers.delete('www-authenticate');
        }
        if (response.status > 399) {
          throw response;
        }
      }
      return response;
    }),
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        return from(authService.refreshAccessToken()).pipe(
          switchMap(async () => {
            const newToken = await authService.getAccessToken();
            return request.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } });
          }),
          switchMap(retryRequest => next(retryRequest)),
          catchError(() => {
            loginService.logout();
            toastr.error('Sua chave de acesso está expirada, por favor faça Login novamente.');
            return throwError(() => error);
          })
        );
      }
      CustomErrorHandler.handleError(error);
      return throwError(() => error);
    })
  );
};
